import { defineStore } from "pinia";
import { ref, reactive, computed, watch, inject } from "vue";
import { useFetch } from "@/helpers/composables/useFetch";
import { router } from "@inertiajs/vue3";
import { useDebounceFn, useStorage } from '@vueuse/core';
import { useLists } from "@/helpers/composables/useList";


export const useCheckoutD2CStore = ()=>{
    return defineStore("checkoutD2CStore", ()=>{

        let route = inject('route');
        let { applyCouponOnList, fetchRestrictions } = useLists({ router, route });

        const couponCode = useStorage("coupon",null);
        const couponInformation = ref({});
    
        // TAX
        const taxAmount = ref(0);
        const isLoadingTax = ref(false);
        const taxPayload = ref({
            zip: null,
            list_id: null,
            shipping: null,
            address1: null,
            address2: null,
            city: null,
            state: null,
            used_coupon: null,
            email: null,
        })
        const setTaxPayload = (form)=>{
            taxPayload.value = {
                zip: form?.shipping_zip,
                list_id: form?.list_id,
                shipping: form?.shipping_option?.cost ?? 0,
                address1: form?.shipping_address,
                address2: form?.shipping_address2,
                city: form?.shipping_city,
                state: form?.shipping_state,
                used_coupon: couponCode.value,
                email: form?.email,
            }
        }

        const { 
            data:taxData, 
            execute:executeGetTax
        } = useFetch(route('get.tax'), {
            immediate: false,
            afterFetch: async (ctx) => {
                isLoadingTax.value = false;
                taxAmount.value = ctx?.data?.taxAmount;
            },
            onError: () => {
                isLoadingTax.value = false;
            }
        }).post(taxPayload).json();

        const fetchTax = useDebounceFn(()=>{
            executeGetTax();
        }, 500);

        const recalculateTax = ()=>{
            // console.log("GET TAX")
            isLoadingTax.value = true;
            fetchTax();
        };

        // SHIPPING RATES
        const shippingStateForRates = ref(null);
        const restrictions = ref([]);

        const shippingRatesPayload = computed(()=>({
            used_coupon: couponCode.value,
            state: shippingStateForRates?.value ?? null
        }))
        const isLoadingShippingRates = ref(false);
        const isLoadingRestrictions = ref(false);
        const isUpdatingCartItem = ref(false);
        const { 
            data:shippingRates, 
            execute:executeGetShippingRates
        } = useFetch(route('update.shipping'), {
            immediate: false,
            afterFetch: async (ctx) => {
                isLoadingShippingRates.value = false;
            },
            onError: () => {
                isLoadingShippingRates.value = false;
            }
        }).post(shippingRatesPayload).json();

        const fetchShippingRates = useDebounceFn(()=>{
            executeGetShippingRates();
        }, 500);

        const getShippingRates = ()=>{
            // console.log("GET SHIPPING RATES");
            isLoadingShippingRates.value = true;
            fetchShippingRates();
        };

        const checkRestrictions = (payload)=>{
            isLoadingRestrictions.value = true;
            fetchRestrictions(payload, {
                afterFetch: (ctx) => {
                    restrictions.value = ctx.data;
                    isLoadingRestrictions.value = false;
                },
                onFetchError: ()=>{
                    isLoadingRestrictions.value = false;
                }
            });
        };

        // COUPON
        const applyCouponAndRecalculate = (couponCodeInput, skipRecalculation=false)=>{
            // console.log("APPLY COUPON", couponCodeInput)
            if(!['',null,undefined].includes(couponCodeInput?.trim())){
                applyCouponOnList(couponCodeInput,{
                    afterFetch: (ctx)=>{
                        couponInformation.value = ctx.data;
                        if(couponInformation.value.applied){
                            couponCode.value = couponCodeInput;
                            if(!skipRecalculation){
                                recalculateTax();
                                getShippingRates();
                            }
                        }
                    },
                    onFetchError: ()=>{
                        couponInformation.value = {
                            applied: false,
                            message: "There was an issue applying this coupon. Please try again."
                        }
                    }
                })
            }
        }
        const reapplyCoupon = ()=>{
            // console.log("REAPPLY")
            applyCouponAndRecalculate(couponCode.value, true);
        }

        const hasShippingAddress = computed(()=>{
            return ['address1','city','state','zip'].every(field=>{
                return !['',null,undefined].includes(taxPayload.value[field]);
            })
        })

        // ORDER
        const reloadOrderInfo = ()=>{
            // console.log("RELOAD ORDER INFO")
            router.reload({ 
                only: [
                    'checkoutResource',
                    'current_list',
                    'user'
                ],
            })
        }

        const isLoadingCalculation = computed(()=>{
            return isLoadingTax.value || isLoadingShippingRates.value || isLoadingRestrictions.value;
        })

        const reset = ()=>{
            taxPayload.value = {};
            couponInformation.value = {};
            taxAmount.value = 0;
            isLoadingTax.value = false;
        }
    
        return {
            shippingStateForRates,
            taxPayload,
            setTaxPayload,
            taxAmount,
            couponInformation,
            recalculateTax,
            getShippingRates,
            shippingRates,
            applyCouponAndRecalculate,
            reapplyCoupon,
            reloadOrderInfo,
            isLoadingCalculation,
            hasShippingAddress,
            reset,
            checkRestrictions,
            restrictions,
            isUpdatingCartItem
        }
    })();
}
